/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AntFlowProcessList, Project } from '@/types/models.ts';
import { classNames } from 'primereact/utils';
import {
  CopyText,
  DataTableColumns,
  defaultProcessDatableColumns,
  Job,
  linksDefinition,
  ProcessDescription,
  ProcessDetailViewer,
  ProcessesDataTable,
  ProcessesViewerProps,
  ProcessSummary,
  useProcessesContext
} from 'ogc-processes-components';
import { History, Leaf } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import { carbonDescription } from '@/components/ProcessesWrapper/CaboneScore';
import { ProcessCarboneScore } from '@/components/ProcessesWrapper/ProcessCarboneScore.tsx';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { DataTableFilterMeta } from 'primereact/datatable';
import { Utils } from '@/utils/utils.ts';
import { AppRoutes } from '@/constants/routes.ts';
import { NavLink, useNavigate } from 'react-router';
import { processCommander, useQueryFilters } from '@/hooks/useQueryFilters.tsx';
import { Button } from 'primereact/button';
import { ProcessCWLDescription } from '@/components/ProcessesWrapper/ProcessCWLDescription';
import { ProjectProcessesErrors } from './ProjectProcessesErrors';
import { useProjectPath } from '@/hooks/useProjectPath';
import { useStoreContext } from '@/contexts/StoreContext';
import { useAuthService } from '@/hooks/useAuthService';
import { AntFlowExecuteProcessForm } from '../AntFlowExecuteProcessForm';

interface Props {
  project: Project;
  className?: string;
}

export const ProjectProcesses: React.FC<Props> = ({ project, className }) => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const { toBrowserProjectPath } = useProjectPath();

  const { currentUser } = useStoreContext();

  const { processesEndpoint } = useProcessesContext();

  const { injectedToken } = useAuthService();

  const { prefilters, prefilterIsLoading } = useQueryFilters({ commander: processCommander, filter_prefix: 'process' });

  const [runningProcess, setRunningProcess] = React.useState<ProcessDescription>();

  const [activeProcess, setActiveProcess] = useState<ProcessSummary>();

  const toast = useRef<Toast>(null);

  const [processesList, setProcessesList] = useState<AntFlowProcessList>();
  const [isFetching, setIsFetching] = useState(false);

  const fetchProcesses = useCallback(async () => {
    setIsFetching(true);
    const processes_link = project.links.find((link) => link.rel === 'processes')?.href;
    if (!processes_link) {
      setIsFetching(false);
      return;
    }
    const responses = await processesEndpoint.get<AntFlowProcessList>(processes_link + '?include_errors=true');
    if (responses && responses.processes.length > 0) {
      // select process from query param or first one
      const processes_search = responses.processes.find((el) => el.id === prefilters?.global);
      if (Object.keys(prefilters).length > 0) {
        if (processes_search) {
          setActiveProcess(processes_search);
        }
      } else {
        setActiveProcess(responses.processes[0]);
      }
    }
    setProcessesList(responses);
    setIsFetching(false);
  }, [processesEndpoint, project, prefilters]);

  useEffect(() => {
    if (!prefilterIsLoading) {
      fetchProcesses();
    }
  }, [prefilterIsLoading, fetchProcesses]);

  const { mutateAsync: getProcess } = useMutation({
    mutationKey: ['get_process_desc', runningProcess],
    mutationFn: async (processSum: ProcessSummary) => {
      const self_link = processSum.links?.find((el) => el.rel === linksDefinition.process);
      if (self_link) {
        return processesEndpoint.get<ProcessDescription>(self_link.href);
      }
    }
  });

  const { mutateAsync } = useMutation({
    mutationKey: ['create_job', runningProcess],
    mutationFn: async (data: Record<string, unknown>) => {
      if (!runningProcess) {
        throw new Error('current process is not defined');
      }
      let url = runningProcess.id;
      const execution_link = runningProcess.links?.find((el) => el.rel === linksDefinition.execution);
      if (execution_link) {
        url = execution_link.href;
      } else {
        const self_link = runningProcess.links?.find((el) => el.rel === linksDefinition.self);
        url = self_link ? self_link.href : url;
      }
      return processesEndpoint.post_process(url, data);
    }
  });

  const useLink = useMemo(() => {
    if (!activeProcess) {
      return undefined;
    }
    const edit_link = activeProcess.links?.find((el) => el.rel === linksDefinition.editlink);
    const permalink_link = activeProcess.links?.find((el) => el.rel === linksDefinition.permalink);
    return {
      editLink: edit_link?.href,
      permalink: permalink_link?.href
    };
  }, [activeProcess]);

  const updateProcessDescWithSecretHelper = useCallback(
    async (processDesc: ProcessDescription) => {
      return Utils.overrideDescription(
        processDesc,
        t(['processes.inputs.secret-helper'], { link: currentUser?.profile || '#' })
      );
    },
    [t, currentUser]
  );

  const handleOpenRunning = useCallback(
    (item: ProcessSummary) => {
      getProcess(item).then(async (processDesc) => {
        if (processDesc) {
          const updatedDesc = await updateProcessDescWithSecretHelper(processDesc);
          setRunningProcess(updatedDesc);
        }
      });
    },
    [getProcess, updateProcessDescWithSecretHelper]
  );

  const processActions: ProcessesViewerProps['actions'] = useMemo(() => {
    return [
      {
        severity: 'success',
        element: 'pi pi-play',
        help: 'Run process',
        action: handleOpenRunning
      },
      {
        element: (props) => {
          const item = props?.item;
          if (!item) {
            return null;
          }
          const url =
            AppRoutes.jobs.processHistory.url(item.id) + Utils.buildNewQueryParamsUrl({ job_reference: project.name });
          return (
            <NavLink to={url}>
              <Button
                tooltip={t(['actions.show_job_history', 'Job history'])}
                tooltipOptions={{ showDelay: 1000, position: 'bottom', className: 'text-xs' }}
                icon={<History size={16} />}
                rounded
                pt={{ root: { style: { height: '35px', width: '35px' } } }}
              />
            </NavLink>
          );
        }
      }
    ];
  }, [project, t, handleOpenRunning]);

  const updateFilters = (filters: DataTableFilterMeta, project_link: string, deferred = false) => {
    const fn = (f: DataTableFilterMeta) => {
      const params = Utils.filtersToQueryParams(f, 'process');
      navigate(
        AppRoutes.projects.detail.url(toBrowserProjectPath(project_link)) + Utils.buildWithQueryParamsUrl(params)
      );
    };
    if (activeProcess) {
      setActiveProcess(undefined);
    }
    if (deferred) {
      setTimeout(() => {
        fn(filters);
      }, 1200);
      return;
    }
    fn(filters);
  };

  const columns: DataTableColumns[] = useMemo(() => {
    return [
      ...defaultProcessDatableColumns!
        .filter((el) => !['jobControlOptions', 'version', 'id'].includes(el.field ?? ''))
        .map((column) => ({
          ...column,
          header: t([`processes.properties.${column.field}`, `${column.header}`])
        })),
      {
        order: 0,
        field: 'id',
        header: 'ID',
        style: { maxWidth: '350px' },
        body: ({ id }) => {
          const stripPrefix = Utils.hideProcessPrefix(id);
          return <CopyText text={stripPrefix} copyText={id} />;
        }
      },
      {
        order: 5,
        field: 'score',
        align: 'center',
        alignHeader: 'center',
        header: (props) => {
          const { className } = props.props;
          return (
            <div className={classNames(className, 'flex align-items-center')}>
              <i id="carbon-info" className="pi pi-info-circle mr-1" />
              <span className="mr-1">Carbon score</span>
              <Leaf stroke={'white'} fill={'var(--green-500)'} size={15} />

              <Tooltip position="bottom" target="#carbon-info">
                {carbonDescription[i18n.language]}
              </Tooltip>
            </div>
          );
        },
        body: (data: ProcessSummary) => {
          return <ProcessCarboneScore showUnknown help={false} process={data} />;
        }
      }
    ];
  }, [i18n.language, t]);

  const jobDetailLink = (job: Job): string => {
    const link = AppRoutes.jobs.detail.url(job.jobID) + Utils.buildNewQueryParamsUrl({ job_reference: project.name });
    return link;
  };

  const projectLinks = useMemo(() => {
    return {
      project: project.links.find((link) => link.rel === 'project')!.href,
      processes: project.links.find((link) => link.rel === 'processes')!.href
    };
  }, [project]);

  return (
    <section className={classNames(className, 'w-full')}>
      <Toast ref={toast} />

      {processesList?.errors && processesList.errors.length > 0 && (
        <ProjectProcessesErrors errors={processesList.errors} />
      )}

      <div className={'flex flex-column align-items-start w-full'}>
        <ProcessesDataTable
          onRefresh={{
            command: fetchProcesses
          }}
          defaultFilters={prefilters}
          handleGlobalFilters={(filters) => updateFilters(filters, projectLinks.project, true)}
          onClearFilters={{
            command: (filters) => updateFilters(filters, projectLinks.project)
          }}
          dataTableClassName="text-sm"
          columns={columns}
          getCapabilities={{
            title: 'OGC API Processes',
            link: projectLinks.processes,
            formatter: processesEndpoint.hrefFormatter
          }}
          loading={isFetching}
          selection={activeProcess}
          onSelectionChange={(e) => {
            setActiveProcess(e.value);
          }}
          processes={processesList?.processes ?? []}
          actions={processActions}
        />
        {activeProcess && (
          <ProcessDetailViewer
            permalink={useLink?.permalink}
            editLink={useLink?.editLink}
            onRedirect={(job: Job) => {
              navigate(jobDetailLink(job));
            }}
            onImportJobExecutionContent={(job) => {
              const _job: Job = job;
              _job.execution_content = Utils.filterExecutionContent({ job: _job, process: activeProcess });
              return _job;
            }}
            viewProcessDefinition
            extraInputsToIgnore={['antflow_config:']}
            cwlViewer={<ProcessCWLDescription process={activeProcess} project={project} />}
            process={activeProcess}
            hrefFormatter={async (link: string) => {
              return await injectedToken(link);
            }}
            onFormatProcessDescription={updateProcessDescWithSecretHelper}
          />
        )}

        {runningProcess && (
          <Dialog
            header={runningProcess.title}
            visible={!!runningProcess}
            style={{ width: '60%', minHeight: '15rem' }}
            onHide={() => {
              setRunningProcess(undefined);
            }}
          >
            <div style={{ minHeight: '25rem', height: '100%' }} className="w-full">
              <AntFlowExecuteProcessForm
                description={runningProcess.description}
                inputs={runningProcess?.inputs}
                links={runningProcess?.links}
                onImportJobExecutionContent={(job) => {
                  const _job: Job = job;
                  _job.execution_content = Utils.filterExecutionContent({ job: _job, process: runningProcess });
                  return _job;
                }}
                onSubmit={({ formData }) => {
                  return mutateAsync(formData).then(async (resp) => {
                    if (resp) {
                      toast.current?.show({
                        severity: 'success',
                        summary: t(['notifs.success.title'], 'Success'),
                        detail: (
                          <div className="flex flex-column">
                            <div>{t(['notifs.success.description'], 'Operation success')}</div>
                            <NavLink className="text-color" target="_blank" to={jobDetailLink(resp as Job)}>
                              {t(['actions.see_job'], 'See more')}
                            </NavLink>
                          </div>
                        ),
                        contentClassName: 'text-left',
                        className: 'text-left',
                        life: 8000
                      });
                      setRunningProcess(undefined);
                      return true;
                    } else {
                      toast.current?.show({
                        severity: 'error',
                        summary: t(['notifs.error.title'], 'Error'),
                        detail: t(['notifs.error.description'], 'Operation declined'),
                        contentClassName: 'text-left',
                        className: 'text-left',
                        life: 6000
                      });
                    }
                    return false;
                  });
                }}
              />
            </div>
          </Dialog>
        )}
      </div>
    </section>
  );
};
